import React, { useEffect, useState } from 'react';
import { RadioProvider, useRadio } from '../../contexts/LazyRadioContext';
import { SOCKET_URL } from '../../lib/env';
import DispatcherConsole from './DispatcherConsole';
import UnitRadio from './UnitRadio';
import './RadioInterface.css';

// Component to handle radio initialization
const RadioInitializer: React.FC<{ token: string }> = ({ token }) => {
  const { initialize } = useRadio();
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (token && !hasInitialized) {
      setHasInitialized(true);
      initialize(token).catch((error) => {
        console.error('Failed to initialize radio:', error);
      });
    }
  }, [token, initialize, hasInitialized]);

  return null; // This component doesn't render anything
};

interface RadioInterfaceProps {
  token: string;
  serverUrl?: string;
}

const RadioInterfaceContent: React.FC = () => {
  const { user, isInitialized, error } = useRadio();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isInitialized) {
      setIsLoading(false);
    }
  }, [isInitialized]);

  if (isLoading) {
    return (
      <div className="radio-loading">
        <div className="loading-spinner"></div>
        <div className="loading-text">Initializing Radio System...</div>
        <div className="loading-subtext">Connecting to WebRTC infrastructure</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="radio-error">
        <div className="error-icon">⚠️</div>
        <div className="error-title">Connection Failed</div>
        <div className="error-message">{error}</div>
        <button 
          className="retry-button"
          onClick={() => window.location.reload()}
        >
          Retry Connection
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="radio-error">
        <div className="error-icon">🔒</div>
        <div className="error-title">Authentication Failed</div>
        <div className="error-message">Unable to authenticate with radio system</div>
        <button 
          className="retry-button"
          onClick={() => window.location.reload()}
        >
          Retry Authentication
        </button>
      </div>
    );
  }

  // Render appropriate interface based on user role
  if (user.role === 'dispatcher') {
    return <DispatcherConsole />;
  } else if (user.role === 'unit') {
    return <UnitRadio />;
  } else {
    return (
      <div className="radio-error">
        <div className="error-icon">❌</div>
        <div className="error-title">Invalid Role</div>
        <div className="error-message">User role '{user.role}' is not supported</div>
      </div>
    );
  }
};

// Wrapper component that uses the radio context
const RadioInterfaceWrapper: React.FC<{ token: string }> = ({ token }) => {
  return (
    <div className="radio-interface">
      <RadioInitializer token={token} />
      <RadioInterfaceContent />
    </div>
  );
};

const RadioInterface: React.FC<RadioInterfaceProps> = ({ token, serverUrl }) => {
  if (!token) {
    return (
      <div className="radio-error">
        <div className="error-icon">🔑</div>
        <div className="error-title">No Token</div>
        <div className="error-message">Authentication token is required</div>
      </div>
    );
  }

  return (
    <RadioProvider serverUrl={serverUrl || SOCKET_URL}>
      <RadioInterfaceWrapper token={token} />
    </RadioProvider>
  );
};

export default RadioInterface;
